import { cloneElement, isValidElement, useEffect, useId, useRef, type ReactNode } from 'react';

/** A labelled input with its error and hint beside (not inside) the label, so the input's name stays just the label. The error and hint are linked to a single input child for screen readers. */
export function Field(props: {
  label: string;
  error?: string;
  hint?: string;
  children: ReactNode;
}) {
  const id = useId();
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;
  const describedBy = [props.hint ? hintId : '', props.error ? errorId : '']
    .filter(Boolean)
    .join(' ');
  const child =
    describedBy && isValidElement(props.children)
      ? cloneElement(props.children as React.ReactElement<Record<string, unknown>>, {
          'aria-describedby': describedBy,
          ...(props.error ? { 'aria-invalid': true } : {}),
        })
      : props.children;
  return (
    <div className={props.error ? 'field invalid' : 'field'}>
      <label>
        {props.label}
        {child}
      </label>
      {props.hint && (
        <span className="muted" id={hintId}>
          {props.hint}
        </span>
      )}
      {props.error && (
        <span className="error" id={errorId}>
          {props.error}
        </span>
      )}
    </div>
  );
}

/** The list of what went wrong. With `focus` it takes focus when problems appear, so a screen reader announces it. */
export function Problems(props: { problems: string[]; title?: string; focus?: boolean }) {
  const box = useRef<HTMLDivElement>(null);
  // Keyed on the text, not the array: callers build a new array every render, and typing must not pull focus back here.
  const text = props.problems.join('|');
  useEffect(() => {
    if (props.focus && text !== '') box.current?.focus();
  }, [props.focus, text]);
  if (props.problems.length === 0) return null;
  return (
    <div role="alert" className="error box" ref={box} tabIndex={props.focus ? -1 : undefined}>
      <strong>{props.title ?? 'Not saved.'}</strong>
      <ul>
        {props.problems.map((p) => (
          <li key={p}>{p}</li>
        ))}
      </ul>
    </div>
  );
}
